import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../../store/store.context";
import { formatHeader } from "../../utils/helper";
import {
  CASE_DETAILS_CONSTANTS,
  validationOutcomeTableHeader,
} from "../../constants/constants";
import CustomTable from "../common/custom-table/custom-table";
import CaseDetailsHeader from "./case-details-header/case-details-header";
import EmailGeneration from "../common/email-generation/email-generation";
import InvoiceDetails from "./invoice-details/invoice-details";
import PopDetails from "./pop-details/pop-details";
import { renderDocx, renderImage, renderPdf } from "./pop-details/pop-types";
import CustomAccordion from "../common/accordion/custom-accordion";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/mc-loading-indicator";

import "./case-details.scss";
import InvoiceTables from "../InvoiceTables";
import Shipmentdetails from "../Shipmentdetails";
import { API_URL, GET_FILE_API } from "../../services/api-config";

const UNQUALIFIED_RENDER_MAP = {
  pdf: renderPdf,
  docx: renderDocx,
  png: renderImage,
  jpeg: renderImage,
  jpg: renderImage,
};

const CaseDetails = ({ caseDetailsData, onBackClick }) => {
  const { store, updateStore } = useStore();
  const chatContainerRef = useRef(null);
  const emailRef = useRef(null);
  const [mergedDetails, setMergedDetails] = useState(caseDetailsData || {});
  const [validationCount, setValidationCount] = useState(
    typeof caseDetailsData?.validationcount === 'number' ? caseDetailsData.validationcount : undefined
  );
  const [refreshNonce, setRefreshNonce] = useState(0);
  const refreshCaseDetails = () => {
    setRefreshNonce((n) => n + 1);
    const id = caseDetailsData?.casenumber || caseDetailsData?.caseid;
    if (id) {
      updateStore({
        promptQuery: `Show me the case details for ${id}`,
        promptQueryNonce: (store?.promptQueryNonce || 0) + 1,
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 0);
  }, []);

  // Scroll to top when a new case is loaded (by ID),
  // but avoid re-scrolling on every small data update.
  useEffect(() => {
    const id = caseDetailsData?.casenumber || caseDetailsData?.caseid;
    if (!id) return;
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 0);
  }, [caseDetailsData]);

  useEffect(() => {
    setMergedDetails(caseDetailsData || {});
    const { casestatus: status } = caseDetailsData || {};
    const currentUpper = (store?.detailsStatus || '').toUpperCase();
    // Preserve in-session APPROVE/REJECT decisions, and also preserve *-Reopened / NON-AFR
    // statuses (pre-seeded from row click) if the API returns just the base status due to lag.
    const isReopenedOrNonAFR = currentUpper.endsWith('-REOPENED') || currentUpper === 'NON-AFR' || currentUpper === 'NON-AFR-REOPENED';
    const shouldPreserve = currentUpper === 'APPROVED' || currentUpper === 'REJECTED' || isReopenedOrNonAFR;
    updateStore({
      detailsStatus: shouldPreserve ? store.detailsStatus : status,
      caseId: caseDetailsData?.casenumber,
      initialCaseSelect: false,
      status: {
        mail: false,
        casestatus: "",
        mailContent: "",
      },
    });
  }, [caseDetailsData]);

  // Fetch latest case details (including validationcount) and merge into local state
  useEffect(() => {
    const id = caseDetailsData?.casenumber || caseDetailsData?.caseid;
    if (!id) return;
    let isCancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/getcasedetails/${encodeURIComponent(id)}`);
        const json = await resp.json().catch(() => ({}));
        // API may wrap payload as { statusCode, body: { data } }
        const data = json?.body?.data ?? json?.data ?? json;
        if (!isCancelled && data && typeof data === 'object') {
          setMergedDetails(prev => ({ ...prev, ...data }));
          if (Object.prototype.hasOwnProperty.call(data, 'validationcount')) {
            const vc = data.validationcount;
            if (typeof vc === 'number') {
              setValidationCount(vc);
            }
          }
        }
      } catch (e) {
        // Non-fatal: keep existing details
        // console.warn('Case details fetch failed', e);
      }
    })();
    return () => { isCancelled = true; };
  }, [caseDetailsData?.casenumber, caseDetailsData?.caseid, refreshNonce]);


  useEffect(() => {
    if (store?.status?.mail) {
      setTimeout(() => {
        if (emailRef.current) {
          emailRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 0);
    }
  }, [store?.status?.mail]);

  // Keep validationCount in sync if prop changes with a number
  useEffect(() => {
    const vc = caseDetailsData?.validationcount;
    if (typeof vc === 'number') {
      setValidationCount(vc);
    }
  }, [caseDetailsData?.validationcount]);

  const [popDocTypes, setPopDocTypes] = useState([]);
  const [popDocTypesLoaded, setPopDocTypesLoaded] = useState(false);
  const [paymentProofDocs, setPaymentProofDocs] = useState([]);
  useEffect(() => {
    const id = caseDetailsData?.casenumber;
    if (!id) { setPopDocTypes([]); setPaymentProofDocs([]); setPopDocTypesLoaded(false); return; }
    let cancelled = false;
    setPopDocTypesLoaded(false);
    fetch(`${API_URL}/payment_proof_details/${encodeURIComponent(id)}`, {
      headers: { Authorization: `Bearer ${store.authToken}` }
    })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        const docs = json?.body?.pop_documents;
        const list = Array.isArray(docs) ? docs : [];
        setPaymentProofDocs(list);
        setPopDocTypes(list.map((d) => d?.documenttype).filter(Boolean));
      })
      .catch(() => { if (!cancelled) { setPopDocTypes([]); setPaymentProofDocs([]); } })
      .finally(() => { if (!cancelled) setPopDocTypesLoaded(true); });
    return () => { cancelled = true; };
  }, [caseDetailsData?.casenumber, store.authToken, refreshNonce]);

  const unqualifiedPopRows = useMemo(() => {
    const target = (CASE_DETAILS_CONSTANTS.UNQUALIFIED_POP_DOCTYPE || "").toLowerCase();
    return paymentProofDocs.filter(
      (d) => typeof d?.documenttype === "string" && d.documenttype.trim().toLowerCase() === target
    );
  }, [paymentProofDocs]);

  const {
    customername: customerName,
    createddate,
    customerbankaccount: customerBankAccount,
    shipmenttype: direction,
    popdocuments,
    validationstatus,
    countryid,
  } = mergedDetails || {};

  const isUnqualifiedPop = useMemo(() => {
    if (popDocTypes.length === 0) return false;
    const target = (CASE_DETAILS_CONSTANTS.UNQUALIFIED_POP_DOCTYPE || "").toLowerCase();
    return popDocTypes.every((v) => typeof v === "string" && v.trim().toLowerCase() === target);
  }, [popDocTypes]);

  const filteredValidationStatus = useMemo(() => {
    if (!validationstatus || validationstatus.length === 0) {
      return [];
    }

    const maxIteration = Math.max(...validationstatus.map(item => item.rule_iteration || 0));
    return validationstatus
      .filter((item) => item.outcome !== "" && item.rule_iteration === maxIteration)
      .sort((a, b) => a.priority - b.priority);
  }, [validationstatus]);

  const validationHeaders = useMemo(() => (
    validationOutcomeTableHeader.map((key) => ({
      key,
      header: formatHeader(key),
    }))
  ), []);

  const formatLongDate = (value) => {
    if (!value) return "";
    const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    
    if (dateOnlyMatch) {
      // Parse YYYY-MM-DD parts as local timezone to avoid off-by-one-day errors
      const year = parseInt(dateOnlyMatch[1], 10);
      const month = parseInt(dateOnlyMatch[2], 10) - 1; // JavaScript months are 0-indexed
      const day = parseInt(dateOnlyMatch[3], 10);
      const parsed = new Date(year, month, day);
      
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
    
    // Fallback for non-YYYY-MM-DD formats
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";
  const valueOrDash = (value, highlightIfEmpty = false) => {
    if (hasValue(value)) return value;
    return <span style={{ color: highlightIfEmpty ? "#111" : "#999" }}>&mdash;</span>;
  };
  const pickFirst = (...values) => values.find(hasValue);

  const renderUnqualifiedPaymentDetails = (row) => {
    const paymentDate = formatLongDate(
      pickFirst(
        row?.payment_proof_date,
        row?.transactiondate,
        row?.transaction_date,
        row?.valuedate,
        row?.value_date
      )
    );

    const sections = [
      {
        title: "Payer Information",
        rows: [
          ["Payer Name :", pickFirst(row?.payer_name, row?.payerName)],
          ["Payer Account No :", pickFirst(row?.payer_account_number, row?.payer_account_no, row?.payerAccount)],
          ["Country :", pickFirst(row?.country, mergedDetails?.country)],
          ["Payment Date :", paymentDate, true],
        ],
      },
      {
        title: "Beneficiary Information",
        rows: [
          ["Beneficiary :", pickFirst(row?.beneficiary_name, mergedDetails?.beneficiaryname)],
          ["Bank Account No. (IBAN) :", pickFirst(row?.bank_account_number, mergedDetails?.beneficiaryaccount), true],
          ["Amount :", pickFirst(row?.amount_in_payment_proof, row?.amountInPaymentProof, row?.paymentamount, mergedDetails?.paymentamount), true],
          ["POP Currency :", pickFirst(row?.currency_in_payment_proof, row?.currencyInPaymentProof, row?.pop_currency, mergedDetails?.popcurrency)],
        ],
      },
    ];

    return (
      <div className="details-section">
        {sections.map(({ title, rows }) => (
          <div className="details-columns" key={title}>
            <p className="details-title">{title}</p>
            {rows.map(([label, value, highlightIfEmpty]) => (
              <div className="detail-row" key={label}>
                <p
                  className="label"
                  style={
                    highlightIfEmpty && !hasValue(value)
                      ? { backgroundColor: "#F7CDD2", color: "#111" }
                      : undefined
                  }
                >
                  {label}
                </p>
                <p
                  className="value"
                  style={
                    highlightIfEmpty && !hasValue(value)
                      ? { backgroundColor: "#F7CDD2", color: "#111" }
                      : undefined
                  }
                >
                  {valueOrDash(value, highlightIfEmpty)}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  if (!mergedDetails || Object.keys(mergedDetails).length === 0) {
    return (
      <div className="case-details-container">
        <p>{CASE_DETAILS_CONSTANTS.MESSAGES.CASE_NOT_FOUND}</p>
      </div>
    );
  }

  return (
    <div className="case-details-container">
      <CaseDetailsHeader caseDetailsInfo={mergedDetails} onBackClick={onBackClick} />

      <InvoiceDetails caseDetailsInfo={mergedDetails} onCaseRefresh={refreshCaseDetails} />

      <div className="supporting-documents">
        <header className="support-doc-header">
          {CASE_DETAILS_CONSTANTS.HEADERS.SUPPORTING_DOCUMENTS}
        </header>
        {popdocuments?.length > 0 ? (
          popdocuments.map((popdocument, index) => (
            <PopDetails
              key={index}
              fetchPopDetails={popdocument}
              caseNumber={mergedDetails?.casenumber}
              shipmenttype={direction}
              customerName={customerName}
              customerBankAccount={customerBankAccount}
              country={mergedDetails?.country}
              countryId={countryid}
              popIndex={index}
            />
          ))
        ) : !popDocTypesLoaded ? (
          <div className="no-documents-found" style={{ textAlign: "center" }}>
            <McLoadingIndicator appearance="primary" />
          </div>
        ) : isUnqualifiedPop && unqualifiedPopRows.length === 0 ? (
          <div className="no-documents-found">
            <p><b>{CASE_DETAILS_CONSTANTS.MESSAGES.NO_QUALIFIED_POP}</b></p>
          </div>
        ) : unqualifiedPopRows.length === 0 ? (
          <div className="no-documents-found">
            <p>{CASE_DETAILS_CONSTANTS.MESSAGES.NO_SUPPORTING_DOCUMENTS}</p>
          </div>
        ) : null}

        {unqualifiedPopRows.length > 0 && (() => {
          const caseNumber = mergedDetails?.casenumber || "";
          const renderAttachment = (rawFilename) => {
            const filename = String(rawFilename ?? "").trim();
            if (!filename) {
              return <p className="error-text">No documents attached</p>;
            }
            const ext = filename
              .slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2)
              .toLowerCase();
            const renderer = UNQUALIFIED_RENDER_MAP[ext];
            const fileUrl = `${GET_FILE_API(caseNumber)}/${encodeURIComponent(filename)}`;
            if (!renderer) {
              return (
                <p className="error-text">Unsupported document type: {ext}</p>
              );
            }
            return renderer(fileUrl, filename);
          };
          return (
            <>
              {unqualifiedPopRows.map((row, idx) => (
                <div className="pop-details-container" key={row?.popid ?? idx}>
                  <div className="pop-header">
                    <p className="header-text">Attachment</p>
                  </div>
                  <hr className="divider" />
                  {renderUnqualifiedPaymentDetails(row)}
                  <CustomAccordion title={`Unqualified POP ${idx + 1}`}>
                    {renderAttachment(row?.proofdocumentpath)}
                  </CustomAccordion>
                </div>
              ))}
            </>
          );
        })()}

        <InvoiceTables caseDetailsInfo={mergedDetails} />
        <Shipmentdetails caseDetailsInfo={mergedDetails}/>
      </div>

      <header className="support-doc-header">
        {CASE_DETAILS_CONSTANTS.HEADERS.VALIDATION_OUTCOMES}
      </header>
      {filteredValidationStatus && filteredValidationStatus.length > 0 ? (
        <CustomTable
          headers={validationHeaders}
          rows={filteredValidationStatus}
          tableType={"validation"}
          paginationHeader={CASE_DETAILS_CONSTANTS.HEADERS.MANUAL_VALIDATIONS_REVIEW}
          validationcount={typeof validationCount === 'number' ? validationCount : undefined}
        />
      ) : (
        <div className="no-documents-found">
          <p>{CASE_DETAILS_CONSTANTS.MESSAGES.NO_VALIDATION_OUTCOMES}</p>
        </div>
      )}
      <div className="email-generation" ref={emailRef}>
        {store?.status?.mail && <EmailGeneration createdDate={createddate} />}
      </div>
    </div>
  );
};

export default CaseDetails;
